import React, { useState } from "react";

import { faChevronDown, faChevronUp, faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { faFolder } from "@fortawesome/pro-solid-svg-icons";

import { Icon, InputText, Skeleton, Text } from "@performyard/py-components";

import { useCurrentUser } from "../../../api/hooks";
import { useDocuments } from "../../../api/hooks/defs/documents";
import { useSignatureRequestTemplates } from "../../../api/hooks/defs/signature-request-templates";

import { getCurrentUserName, toPickableDocuments, useFolderAccordion } from "../../Documents/documentPicker";
import type { DocumentFile } from "../../Documents/schema";

/**
 * The contract's max limit, and ListSignatureRequestTemplatesQuery has no name filter,
 * so the Signature Templates folder is one unsearchable page. Companies past this many
 * templates get a truncated folder, which the picker says out loud rather than leaving
 * a template silently missing.
 */
const TEMPLATE_LIMIT = 100;

/** What the picker hands back: a signature request template, or a raw document. */
export type SignatureSelection = {
	kind: "template" | "document";
	id: string;
	name: string;
	/**
	 * Signers the selection already defines. Templates carry theirs; a raw document has
	 * none until the signing wizard assigns them, hence null rather than 0 — "no signers
	 * yet" and "a request with zero signers" must not render the same way.
	 */
	signerCount: number | null;
};

type PickerFolder = {
	key: string;
	label: string;
	items: SignatureSelection[];
	/** Set when the folder's contents are capped, to explain a missing entry. */
	truncatedNote?: string;
};

const toDocumentSelections = (docs: DocumentFile[]): SignatureSelection[] =>
	toPickableDocuments(docs).map((doc) => ({ kind: "document" as const, ...doc, signerCount: null }));

type SignatureDocumentPickerProps = {
	/** The workflow's subject, whose personal documents get their own folder. */
	subject: { _id: string; name: string } | null;
	/** Called once, when a document or template is chosen; the picker then unmounts. */
	onSelect: (selection: SignatureSelection) => void;
};

/**
 * Document browser for attaching a signature request to a workflow to-do: company
 * documents, signature request templates, and the personal documents of both the
 * workflow's subject and the current user.
 *
 * Selecting a template and selecting a document are different hand-offs to the legacy
 * signing wizard, which is why the selection carries its `kind` rather than just an id.
 */
const SignatureDocumentPicker = ({ subject, onSelect }: SignatureDocumentPickerProps) => {
	const [searchValue, setSearchValue] = useState("");

	const { data: currentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload?.user_id ?? "";
	const currentUserName = getCurrentUserName();

	const { data: currentUserDocuments, isLoading: isLoadingCurrentUserDocs } = useDocuments(currentUserId);
	// The subject is usually someone else; skip the duplicate request when they aren't,
	// and reuse the current user's payload for their folder below.
	const subjectIsCurrentUser = !!subject && subject._id === currentUserId;
	const { data: subjectDocuments, isLoading: isLoadingSubjectDocs } = useDocuments(
		subject && !subjectIsCurrentUser ? subject._id : ""
	);
	const { data: templatesData, isLoading: isLoadingTemplates } = useSignatureRequestTemplates({
		limit: TEMPLATE_LIMIT,
		sortBy: "name",
		sortOrder: "asc",
	});

	const isLoading = isLoadingCurrentUserDocs || isLoadingSubjectDocs || isLoadingTemplates;

	const templates = (templatesData?.signatureRequestTemplates ?? []).map((template) => ({
		kind: "template" as const,
		id: template._id,
		name: template.name,
		// actorCount is the template's signer count ($size of its signers array).
		signerCount: template.actorCount,
	}));
	const templatesTruncated = (templatesData?.totalCount ?? 0) > templates.length;

	const subjectFolderDocuments = subjectIsCurrentUser ? currentUserDocuments?.myFiles : subjectDocuments?.myFiles;

	const folders: PickerFolder[] = [
		{
			key: "company",
			label: "Company Documents",
			items: toDocumentSelections(currentUserDocuments?.companyFiles ?? []),
		},
		{
			key: "templates",
			label: "Signature Templates",
			items: templates,
			truncatedNote: templatesTruncated
				? `Showing the first ${TEMPLATE_LIMIT} templates by name. Set up this to-do from the document signing page if the template you need isn't listed.`
				: undefined,
		},
		// Only one personal folder when the subject IS the current user — two identically
		// named folders listing the same files would just read as a bug.
		...(subject && !subjectIsCurrentUser
			? [
					{
						key: "subject",
						label: `${subject.name}'s Documents`,
						items: toDocumentSelections(subjectFolderDocuments ?? []),
					},
				]
			: []),
		{
			key: "mine",
			label: currentUserName ? `${currentUserName}'s Documents` : "My Documents",
			items: toDocumentSelections(currentUserDocuments?.myFiles ?? []),
		},
	];

	const search = searchValue.trim().toLowerCase();
	const visibleFolders = folders
		.map((folder) => ({
			...folder,
			items: search ? folder.items.filter((item) => item.name.toLowerCase().includes(search)) : folder.items,
		}))
		// A search hides folders it doesn't match; with no search every folder shows, even
		// an empty one, so its emptiness is visible rather than looking like a missing folder.
		.filter((folder) => !search || folder.items.length > 0);

	const {
		isExpanded: isFolderExpanded,
		toggle: toggleFolder,
		isLocked: foldersLocked,
	} = useFolderAccordion(!!search);

	return (
		// min-h-0 continues the modal's chain — see TodoFormModal.
		// The id is what the Add Signature Request button's aria-controls points at.
		<div
			id="signature-document-picker"
			className="flex min-h-0 flex-col gap-2"
			data-testid="signatureDocumentPicker"
		>
			<InputText
				value={searchValue}
				onChange={setSearchValue}
				placeholder="Search documents..."
				leftIcon={faMagnifyingGlass}
				clearable
				onKeyDown={(event) => {
					if (event.key === "Enter") {
						event.preventDefault();
					}
				}}
				data-testid="signatureDocumentSearch"
			/>

			{isLoading ? (
				<div className="flex flex-col gap-2">
					{[0, 1, 2, 3].map((index) => (
						<Skeleton key={index} className="h-8 w-full" />
					))}
				</div>
			) : (
				// max-h is the preferred height; min-h-0 lets it shrink further so this stays
				// the single scroll region. overflow-y-auto also clips the folder rows' fill
				// to the corner radius.
				<div className="max-h-[220px] min-h-0 overflow-y-auto rounded-md border border-solid border-neutral-2 bg-white">
					{visibleFolders.length === 0 ? (
						<div className="flex h-16 items-center justify-center">
							<Text size="paragraph-3" weight="regular" color="neutral-6">
								No documents match your search
							</Text>
						</div>
					) : (
						visibleFolders.map((folder) => {
							const expanded = isFolderExpanded(folder.key);
							const header = (
								<>
									<Icon icon={faFolder} size={16} color="neutral-7" />
									<span className="min-w-0 flex-1">
										<Text size="paragraph-3" weight="regular" color="neutral-9">
											{folder.label}
										</Text>
									</span>
									{!foldersLocked && (
										<Icon
											icon={expanded ? faChevronUp : faChevronDown}
											size={12}
											color="neutral-5"
										/>
									)}
								</>
							);
							return (
								<div key={folder.key}>
									{foldersLocked ? (
										<div
											className="flex w-full items-center gap-2 bg-neutral-1 px-3 py-2.5 text-left"
											data-testid={`signatureFolder-${folder.key}`}
										>
											{header}
										</div>
									) : (
										<button
											type="button"
											aria-expanded={expanded}
											onClick={() => toggleFolder(folder.key)}
											className="flex w-full items-center gap-2 border-0 bg-neutral-1 px-3 py-2.5 text-left hover:cursor-pointer hover:bg-neutral-2"
											data-testid={`signatureFolder-${folder.key}`}
										>
											{header}
										</button>
									)}

									{expanded && (
										<div className="flex flex-col bg-white">
											{folder.items.length === 0 ? (
												<Text size="paragraph-3" weight="regular" color="neutral-6">
													<span className="block px-3 py-2.5 ps-9">No documents</span>
												</Text>
											) : (
												folder.items.map((item) => (
													<button
														key={`${item.kind}-${item.id}`}
														type="button"
														onClick={() => onSelect(item)}
														className="flex w-full items-center border-0 bg-white px-3 py-2.5 ps-9 text-left hover:cursor-pointer hover:bg-primary-1"
														data-testid={`signatureDocumentOption-${item.id}`}
													>
														<Text size="paragraph-3" weight="regular" color="neutral-7">
															<span className="py-line-clamp-1">{item.name}</span>
														</Text>
													</button>
												))
											)}
											{folder.truncatedNote && (
												<Text
													size="paragraph-3"
													weight="regular"
													color="neutral-6"
													data-testid={`signatureFolderTruncated-${folder.key}`}
												>
													<span className="block px-3 py-2 ps-9">{folder.truncatedNote}</span>
												</Text>
											)}
										</div>
									)}
								</div>
							);
						})
					)}
				</div>
			)}
		</div>
	);
};

export default SignatureDocumentPicker;
